import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import type { TokenPair } from "@/Api/API";
import { useApiHelper } from "@/contexts";
import type { User } from "@/types/user";

export interface AuthContextValue {
	user: User | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	isImpersonating: boolean;
	isAdmin: boolean;
	login: (email: string, password: string) => Promise<User | null>;
	logout: (withoutRedirect?: boolean) => void;
	getAuth: (force?: boolean) => Promise<User | null>;
	loginAsGroup: (uuid: string) => Promise<User | null>;
	loginBackAsUser: () => Promise<User | null>;
	loginAsUser: (userUuid: string) => Promise<User | null>;
	loginBackAsAdmin: () => Promise<User | null>;
	confirmAccount: (uuid: string, password: string) => Promise<void>;
	resetPassword: (token: string, password: string) => Promise<void>;
	registerStopPlayer: (stop: () => void) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const apiHelper = useApiHelper();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [user, setUser] = useState<User | null>(() =>
		JSON.parse(localStorage.getItem("user_infos") || "null")
	);
	const [isLoading, setIsLoading] = useState(false);

	// The audio player lives below this provider in the tree; it registers its
	// stop() so that every token swap (login/impersonation) can halt playback.
	const stopPlayerRef = useRef<(() => void) | null>(null);
	const registerStopPlayer = useCallback((stop: () => void) => {
		stopPlayerRef.current = stop;
	}, []);

	const userRef = useRef(user);
	userRef.current = user;

	const logout = useCallback(
		(withoutRedirect = false) => {
			const refreshToken = apiHelper.api.getRefreshToken();
			if (refreshToken) {
				// Best effort: revoke server-side so the session cannot be
				// resumed from a stolen token.
				void apiHelper.auth.logout(refreshToken).catch(() => {});
			}
			setUser(null);
			apiHelper.api.clearTokens();
			localStorage.removeItem("user_infos");
			if (!withoutRedirect) {
				navigate("/login");
			}
		},
		[apiHelper, navigate]
	);

	const getAuth = useCallback(
		async (force = false) => {
			setIsLoading(true);
			try {
				const current = force ? null : userRef.current;
				const profile = current || (await apiHelper.auth.getProfile());
				localStorage.setItem("user_infos", JSON.stringify(profile));
				setUser(profile);
				return profile;
			} catch (e) {
				// Only a refusal ends the session. A cold start or a network
				// blip used to sign the user out here; now the API layer has
				// already tried to refresh, so reaching this with a 401 means
				// the session really is gone.
				const status = isAxiosError(e) ? e.response?.status : undefined;
				if (status === 401 || status === 403) {
					logout();
				}
				throw e;
			} finally {
				setIsLoading(false);
			}
		},
		[apiHelper, logout]
	);

	// cancelQueries comes first so in-flight queries from the previous
	// persona cannot write stale data into the fresh cache.
	const applyToken = useCallback(
		async (tokenPromise: Promise<TokenPair>) => {
			setIsLoading(true);
			try {
				apiHelper.api.storeTokens(await tokenPromise);
				await queryClient.cancelQueries();
				queryClient.invalidateQueries();
				stopPlayerRef.current?.();
				return await getAuth(true);
			} finally {
				setIsLoading(false);
			}
		},
		[apiHelper, queryClient, getAuth]
	);

	const login = useCallback(
		(email: string, password: string) =>
			applyToken(apiHelper.auth.login(email, password)),
		[apiHelper, applyToken]
	);
	const loginAsGroup = useCallback(
		(uuid: string) => applyToken(apiHelper.auth.loginAsGroup(uuid)),
		[apiHelper, applyToken]
	);
	const loginBackAsUser = useCallback(
		() => applyToken(apiHelper.auth.loginBackAsUser()),
		[apiHelper, applyToken]
	);
	const loginAsUser = useCallback(
		(userUuid: string) => applyToken(apiHelper.auth.loginAsUser(userUuid)),
		[apiHelper, applyToken]
	);
	const loginBackAsAdmin = useCallback(
		() => applyToken(apiHelper.auth.loginBackAsAdmin()),
		[apiHelper, applyToken]
	);

	const confirmAccount = useCallback(
		(uuid: string, password: string) =>
			apiHelper.auth.confirmAccount(uuid, password),
		[apiHelper]
	);
	const resetPassword = useCallback(
		(token: string, password: string) =>
			apiHelper.auth.resetPassword(token, password),
		[apiHelper]
	);

	// The API layer refreshes on 401 and only calls back here when the session
	// is genuinely over.
	useEffect(() => {
		apiHelper.api.registerAuthHandlers({
			onUnauthorized: () => logout(),
			onTokensRenewed: () => {},
		});
	}, [apiHelper, logout]);

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			isLoading,
			isAuthenticated: user?.sub !== undefined,
			isImpersonating: user?.impersonated_by !== undefined,
			isAdmin: user?.is_admin === true,
			login,
			logout,
			getAuth,
			loginAsGroup,
			loginBackAsUser,
			loginAsUser,
			loginBackAsAdmin,
			confirmAccount,
			resetPassword,
			registerStopPlayer,
		}),
		[
			user,
			isLoading,
			login,
			logout,
			getAuth,
			loginAsGroup,
			loginBackAsUser,
			loginAsUser,
			loginBackAsAdmin,
			confirmAccount,
			resetPassword,
			registerStopPlayer,
		]
	);

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) {
		throw new Error("useAuth must be used within AuthProvider");
	}
	return ctx;
}

export function useIsAdmin(): boolean {
	return useAuth().isAdmin;
}

export function useIsImpersonating(): boolean {
	return useAuth().isImpersonating;
}
